import type { EvidenceDetail } from 'api';
import { useMaterialModel } from 'api';
import { useTranslation } from 'i18n';
import { useState } from 'react';
import { EvidenceSessionPhotoGallery, Grid, MediaDialog, Stack, Typography, useBreakpoint } from 'ui';

import { AiFeedbackVisibilityProvider, AiFilters, AnnotatedMediaItem, useAnnotations } from '../../../annotations';
import { AiSampleBreakdown } from '../../../breakdowns';
import { useEvidenceAiSessionDetails } from '../../hooks/useEvidenceAiSessionDetails/useEvidenceAiSessionDetails';

export const EvidenceAiLooseSessionDetails = ({
  date,
  parentContentTypeId,
  parentId,
  uploadedById,
  siteMaterialId,
}: {
  date: string;
  parentContentTypeId: number;
  parentId: number;
  uploadedById: number | null;
  siteMaterialId: number;
}) => {
  const { t } = useTranslation('features');
  const model = useMaterialModel(String(siteMaterialId ?? ''), false);
  const { isMobile } = useBreakpoint();
  const { photoEvidence, evidenceList, totalWeight, aluminiumLooseAIComposition } = useEvidenceAiSessionDetails({
    date,
    parentContentTypeId,
    parentId,
    uploadedById,
  });

  const {
    selectedAnnotation,
    handleImageChange,
    hiddenCategories,
    handleHideCategory,
    handleToggleLegend,
    showLegend,
  } = useAnnotations({ model: model ?? undefined });

  const [selectedEvidence, setSelectedEvidence] = useState<EvidenceDetail | null>(null);

  const carouselMedia = photoEvidence.map((file) => file.fullResolutionSrc);

  const handleSelect = (src: string) => {
    const relatedEvidence = evidenceList.find((item) => item.file_url === src || item.preview_url === src);
    setSelectedEvidence(relatedEvidence || null);
    handleImageChange([...(relatedEvidence?.annotations || [])]);
  };

  return (
    <>
      <Grid container spacing={3} data-testid="evidence-session-details">
        <Grid
          data-testid="photo-evidence"
          size={{
            md: 6,
            xs: 12,
          }}
        >
          <Stack
            sx={{
              gap: 2,
            }}
          >
            <Typography variant="h6" fontWeight="bold">
              {t('backlog.evidence.evidenceAiLooseSessionDetails.sampleImagesTitle')}
            </Typography>
            {photoEvidence.length > 0 ? (
              <EvidenceSessionPhotoGallery
                photoEvidence={photoEvidence}
                maxImages={isMobile ? 6 : 8}
                handleClick={(photo) => handleSelect(photo.src)}
              />
            ) : (
              <Typography>{t('backlog.common.noData')}</Typography>
            )}
          </Stack>
        </Grid>
        <Grid
          data-testid="composition-report"
          size={{
            md: 6,
            xs: 12,
          }}
        >
          <Stack
            sx={{
              gap: 0.5,
            }}
          >
            <Typography variant="h6" gutterBottom fontWeight="bold">
              {t('backlog.evidence.evidenceAiLooseSessionDetails.aiLooseAnalysisTitle')}
            </Typography>
            {aluminiumLooseAIComposition ? (
              <AiSampleBreakdown
                model={'alu_loose'}
                composition={aluminiumLooseAIComposition}
                totalWeight={Number(totalWeight)}
              />
            ) : (
              <Typography>{t('backlog.common.noData')}</Typography>
            )}
          </Stack>
        </Grid>
      </Grid>
      <AiFeedbackVisibilityProvider>
        <MediaDialog
          open={Boolean(selectedEvidence?.file_url)}
          selectedMedium={selectedEvidence?.file_url ?? ''}
          onClose={() => setSelectedEvidence(null)}
          media={carouselMedia}
          handleNavigate={handleSelect}
          imageCount={carouselMedia.length}
          slotProps={{
            imageCarousel: {
              slotProps: {
                ...(selectedAnnotation && selectedEvidence && model
                  ? {
                      mediaItemSlot: (
                        <AnnotatedMediaItem
                          image={selectedEvidence.file_url ?? ''}
                          annotations={selectedAnnotation}
                          hiddenCategories={hiddenCategories}
                          showLegend={showLegend}
                          model={model}
                          showAerosols={true}
                          evidenceId={selectedEvidence.id}
                        />
                      ),
                      sidebar: {
                        children: (
                          <AiFilters
                            selectedAnnotation={selectedAnnotation}
                            hiddenCategories={hiddenCategories}
                            handleHideCategory={handleHideCategory}
                            handleToggleLegend={handleToggleLegend}
                            showLegend={showLegend}
                            model={model}
                            showAerosols
                          />
                        ),
                      },
                    }
                  : null),
              },
            },
          }}
        />
      </AiFeedbackVisibilityProvider>
    </>
  );
};
